// =============================================================================
// useMigrationRun — jeden hook dla przycisku glownego Studio („Przenies") i dla
// podgladu („Zbuduj podglad", branch preview). Analiza -> konto CF (allocator,
// preferuje zapisane pages_cf_account_id) -> runAstroDeploy / runDeploy po
// settings.build_backend. Etykieta przycisku glownego liczona z jednego miejsca
// (primaryAction) — faza 4 „static jako cel publikacji" zmieni tylko te linie.
// =============================================================================

import { useCallback, useMemo, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useCloudflareAccounts } from "@/hooks/useCloudflareAccounts";
import type { MigrationProgressEvent, StaticSiteRow } from "@/services/staticSite/types";
import { runAnalysis, runDeploy, type DeployResult } from "@/services/staticSite/migrationRunner";
import { runAstroDeploy } from "@/services/staticSite/astroDeploy";
import { evaluateAccounts, pickBest, type AccountCandidate } from "@/services/staticSite/cfAccountAllocator";

type PrimaryActionKind = "deploy" | "redeploy" | "publish_changes";

interface PrimaryAction {
  kind: PrimaryActionKind;
  label: string;
}

function primaryActionFor(site: StaticSiteRow | null): PrimaryAction {
  if (!site?.pages_subdomain) return { kind: "deploy", label: "Przenieś na pages.dev" };
  if (site.needs_redeploy) return { kind: "publish_changes", label: "Wdróż zmiany" };
  return { kind: "redeploy", label: "Przenieś ponownie" };
}

export function useMigrationRun(site: StaticSiteRow | null, onDone?: () => void) {
  const { toast } = useToast();
  const { accounts } = useCloudflareAccounts();
  const [busy, setBusy] = useState<null | "deploy" | "preview">(null);
  const [status, setStatus] = useState("");
  const [pct, setPct] = useState<number | null>(null);
  const [blocked, setBlocked] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<DeployResult | null>(null);

  const onProgress = useCallback((ev: MigrationProgressEvent) => {
    if (ev.type === "phase") { setStatus(ev.message); setPct(null); }
    else if (ev.type === "progress") { setStatus(`${ev.message || ""} ${ev.current}/${ev.total}`); setPct(ev.total ? Math.round((ev.current / ev.total) * 100) : null); }
    else if (ev.type === "done") { setStatus(ev.message); setPct(100); }
    else if (ev.type === "error") setStatus(`Błąd: ${ev.message}`);
  }, []);

  const pickAccount = useCallback(async (): Promise<AccountCandidate> => {
    if (!site) throw new Error("Brak migracji");
    if (!accounts.length) throw new Error("Brak kont Cloudflare w panelu /cloudflare — dodaj konto z tokenem (Cloudflare Pages: Edit).");
    setStatus("Wybieram konto Cloudflare…");
    const candidates = await evaluateAccounts(accounts, site.domain);
    const best = candidates.find((c) => c.account.id === site.pages_cf_account_id && !c.error) || pickBest(candidates);
    if (!best) {
      const errs = candidates.filter((c) => c.error).map((c) => `${c.account.label}: ${c.error}`).join("; ");
      throw new Error(`Żadne konto CF nie nadaje się do deployu. ${errs || "Wszystkie mają ≥95 projektów."}`);
    }
    return best;
  }, [site, accounts]);

  /** Pelny przebieg: analiza -> konto -> deploy na main. */
  const run = useCallback(async () => {
    if (!site) return;
    setBusy("deploy");
    setBlocked(null);
    setStatus("Analiza…");
    try {
      const analysis = await runAnalysis(site, onProgress);
      if (analysis.report.blocking) {
        const failed = analysis.report.gates.filter((g) => g.level === "hard" && !g.passed).map((g) => `${g.id} ${g.label}`).join(", ");
        setBlocked(`Zatrzymane przez bramki: ${failed}. Zobacz raport w sekcji Zgodność (brakujące adresy → „Dodaj 301”).`);
        onDone?.();
        return;
      }
      const best = await pickAccount();
      const deployFn = site.settings?.build_backend === "astro" ? runAstroDeploy : runDeploy;
      const res = await deployFn(site, { account: best.account, accountId: best.accountId, trigger: "manual", onProgress });
      setLastResult(res);
      toast({ title: "Strona przeniesiona na adres testowy", description: res.url });
      onDone?.();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setBlocked(msg);
      toast({ title: "Migracja zatrzymana", description: msg, variant: "destructive" });
      onDone?.();
    } finally {
      setBusy(null);
    }
  }, [site, onProgress, pickAccount, toast, onDone]);

  /** Podglad Astro: build na branchu preview (bez analizy, bez zmiany statusu strony). */
  const runPreviewBuild = useCallback(async (): Promise<string | null> => {
    if (!site) return null;
    setBusy("preview");
    setBlocked(null);
    setStatus("Przygotowuję podgląd…");
    try {
      const best = await pickAccount();
      const res = await runAstroDeploy(site, { account: best.account, accountId: best.accountId, trigger: "manual", branch: "preview", onProgress });
      toast({ title: "Podgląd zbudowany", description: res.url });
      onDone?.();
      return res.url;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setBlocked(msg);
      toast({ title: "Podgląd nieudany", description: msg, variant: "destructive" });
      return null;
    } finally {
      setBusy(null);
    }
  }, [site, onProgress, pickAccount, toast, onDone]);

  const primaryAction = useMemo(() => primaryActionFor(site), [site]);

  return { busy, status, pct, blocked, lastResult, run, runPreviewBuild, primaryAction, accounts };
}
